/**
 * FS-784 / FS-782 — the words for a label read and a photo review.
 *
 * One place, because the same outcome is named in three: the photo block on
 * a card (a read that filled the record on its own), the drawer's banner for a
 * review, and the review dialog. An operator who sees "Couldn't tell what
 * equipment this label is from" in one of them has to see the same sentence in
 * the others, and each one carries the server's own code in a `title` so the
 * sentence can always be traced back to what was recorded.
 */

import type {
  EquipmentTargetKind,
  PhotoReviewDecision,
  PhotoReviewRow,
  PhotoReviewTab,
  ReviewFix,
  ReviewFixName,
  ReviewFixPhotoBlock,
} from '../../api/equipmentEdit';
import { own } from '../../utils/ownKey';
import { easternDate } from '../../utils/easternTime';

/** A photo row's value on file, as the server describes it. */
type PhotoReviewRowCurrent = NonNullable<PhotoReviewRow['current']>;

/** A component as an operator says it, lower case for use inside a sentence. */
export function componentWords(component: string | null | undefined): string {
  switch (component) {
    case 'outdoor_condenser':
      return 'outdoor unit';
    case 'indoor_air_handler':
      return 'indoor unit';
    case 'indoor_coil':
      return 'indoor coil';
    case 'package_unit':
      return 'packaged unit';
    default:
      return 'equipment';
  }
}

/** The drawer tab a component's record lives on. */
export function tabOfComponent(component: string | null | undefined): PhotoReviewTab | null {
  switch (component) {
    case 'outdoor_condenser':
      return 'ac';
    case 'indoor_coil':
      return 'coil';
    case 'indoor_air_handler':
    case 'package_unit':
      return 'hvac';
    default:
      return null;
  }
}

/** The tab names, exactly as the drawer's tab buttons spell them. */
export const TAB_WORDS: Record<EquipmentTargetKind, string> = {
  hvac: 'Indoor unit',
  ac: 'Outdoor unit',
  coil: 'Indoor coil (A-coil)',
  filter: 'Filter',
};

/** A record kind inside a sentence: "the outdoor unit record". */
export const KIND_WORDS: Record<PhotoReviewTab, string> = {
  hvac: 'indoor unit',
  ac: 'outdoor unit',
  coil: 'indoor coil',
};

/**
 * Which drawer tab a document path is on. Paths are absolute and the child
 * collections are fixed, so the collection name is the whole answer.
 */
export function kindOfPath(path: string): EquipmentTargetKind {
  if (/\/ac_systems\/[^/]+$/.test(path)) return 'ac';
  if (/\/coils\/[^/]+$/.test(path)) return 'coil';
  if (/\/filters\/[^/]+$/.test(path)) return 'filter';
  return 'hvac';
}

/** Where a proposed value came from, in the operator's words. */
export function sourceWords(source: string | null | undefined, basis?: string | null): string {
  // FS-787: `spec_lookup` is a value from the manufacturer's document, reached
  // through the equipment catalog; its basis is always `catalog`, so it is
  // never also called "catalog-verified".
  if (source === 'spec_lookup') return CATALOG_SOURCE_WORDS;
  const base =
    source === 'ai_photo'
      ? 'Printed on the label'
      : source === 'ai_model_decode'
        ? 'Decoded from the model number'
        : source === 'catalog'
          ? 'From the equipment catalog'
          : source === 'online'
            ? 'Found online'
            : source
              ? source
              : 'Source not recorded';
  return basis === 'catalog' && source !== 'catalog' ? `${base} (catalog-verified)` : base;
}

/** FS-787 — the words for `spec_lookup`, in the review dialog and the drawer's field sources. */
export const CATALOG_SOURCE_WORDS = 'Manufacturer literature (catalog)';

/** Words with their article: "an outdoor unit", "a heat pump", "an Outdoor unit". */
const withArticle = (words: string): string => `${/^[aeiou]/i.test(words) ? 'an' : 'a'} ${words}`;

/**
 * FS-875 — the catalog's `equipment_type` values, as an operator says them.
 * The catalog has its own vocabulary, wider than the four components a label
 * read names: `componentWords` turned an air handler or a heat pump into
 * "equipment".
 */
const CATALOG_TYPE_WORDS: Record<string, string> = {
  air_handler: 'air handler',
  furnace: 'furnace',
  ductless: 'ductless indoor unit',
  indoor_coil: 'indoor coil',
  outdoor_condenser: 'outdoor unit',
  heat_pump: 'heat pump',
  package_unit: 'packaged unit',
};

/**
 * A catalog `equipment_type` in words, lower case for use inside a sentence.
 * Null when the catalog does not say: `unknown`, missing, or a type this
 * console has no words for.
 */
export function catalogTypeWords(equipmentType: string | null | undefined): string | null {
  return own(CATALOG_TYPE_WORDS, equipmentType?.trim().toLowerCase()) ?? null;
}

/**
 * FS-876 — the tab a catalog type belongs on, for a type whose name does not
 * say it: an operator asked whether a heat pump is an outdoor or an indoor
 * unit (FS-942 added the air handler and the furnace). The same place the
 * server's fix moves it to (`COMPONENT_OF_CATALOG_TYPE`).
 */
const CATALOG_TYPE_TAB: Record<string, PhotoReviewTab> = {
  heat_pump: 'ac',
  air_handler: 'hvac',
  furnace: 'hvac',
};

/**
 * FS-787 — the banner and dialog sentence for a catalog review that is not
 * waiting for values: held (the catalog says the model is a different part of
 * the system from the record it is on) or failed (the values could not be
 * prepared). Null for any other state.
 */
export function catalogHeldWords(input: {
  status: string;
  holdReason: string | null | undefined;
  model: string | null | undefined;
  equipmentType: string | null | undefined;
  droppedOn: PhotoReviewTab;
}): NamedOutcome | null {
  const model = input.model?.trim() || 'this model';
  if (input.status === 'held') {
    if (input.holdReason === 'catalog_component_mismatch') {
      const type = catalogTypeWords(input.equipmentType);
      const belongsOn = own(CATALOG_TYPE_TAB, input.equipmentType?.trim().toLowerCase());
      const kind = type ? withArticle(type) : '';
      return {
        text: type && belongsOn && belongsOn !== input.droppedOn
          ? `The catalog says model ${model} is ${kind}. ${kind[0].toUpperCase()}${kind.slice(1)} is ${withArticle(TAB_WORDS[belongsOn])}, but this one is on the ${TAB_WORDS[input.droppedOn]} record`
          : type
            ? `The catalog says model ${model} is ${kind}, but it is on the ${TAB_WORDS[input.droppedOn]} record`
            : `The catalog does not say what kind of equipment model ${model} is, so its values are not offered for the ${TAB_WORDS[input.droppedOn]} record`,
        code: 'catalog_component_mismatch',
      };
    }
    if (input.holdReason === 'model_evidence_removed') {
      // FS-783 §5.6: the model came from a photo that was removed, so the
      // catalog's values would follow an identity nobody can vouch for.
      return {
        text: 'The model number on this record came from a photo that was removed. Check the model, then the catalog values can be offered again.',
        code: 'model_evidence_removed',
      };
    }
    return {
      text: `The catalog values for model ${model} are on hold`,
      code: input.holdReason ?? 'held',
    };
  }
  if (input.status === 'failed') {
    return { text: `Catalog values for model ${model} couldn't be prepared`, code: 'failed' };
  }
  return null;
}

/** The muted line under a row for an alternative reading: "Model number decode says 3". */
export function alternativeWords(source: string, value: string): string {
  const who =
    source === 'ai_model_decode'
      ? 'Model number decode'
      : source === 'ai_photo'
        ? 'The label'
        : source === 'catalog'
          ? 'The catalog'
          : source === 'online'
            ? 'An online listing'
            : source;
  return `${who} says ${value}`;
}

/** A number the way a person would read it on a label: 36000 → "36,000". Format only. */
export function groupedNumber(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return value.toLocaleString('en-US');
  return String(value ?? '');
}

/** FS-1076 — who decided a person's value on file, as a sentence names them. */
const DECIDER_WORDS: Record<NonNullable<PhotoReviewRowCurrent['decided_by']>, string> = {
  owner: 'the owner',
  operator: 'an operator',
};

/**
 * FS-1076 — who set a person's value on file, and on which Eastern day:
 * "set by the owner on 2026-07-22". Without the day when the record does not
 * say, and "a person" when the server did not say who.
 */
export function decidedWords(current: Pick<PhotoReviewRowCurrent, 'decided_by' | 'decided_at'>): string {
  const who = current.decided_by ? DECIDER_WORDS[current.decided_by] : 'a person';
  return current.decided_at ? `set by ${who} on ${easternDate(current.decided_at)}` : `set by ${who}`;
}

export interface NamedOutcome {
  text: string;
  /** The server's code behind the sentence, for the `title`. */
  code: string;
}

/**
 * A router reason (a held review's `hold.reason`, or a request's
 * `reason_code`) in words, or null when it has none of its own.
 */
export function reasonWords(reason: string | null | undefined): string | null {
  if (!reason) return null;
  if (reason.startsWith('label_kind_')) {
    return "This is a parts or service sticker, not the equipment's rating plate";
  }
  switch (reason) {
    case 'below_component_floor':
    case 'no_component':
    case 'unresolved':
      return "Couldn't tell what equipment this label is from";
    case 'catalog_analyzer_disagreement':
    case 'conflict':
      return 'The catalog and the label disagree about what this is';
    case 'no_hvac_document':
      return 'No HVAC record to attach it to';
    case 'multiple_ac_children':
      return "Several outdoor units; couldn't tell which";
    default:
      return null;
  }
}

/**
 * How a finished request came out, named (contract §8.6). Null when the
 * request carries no `outcome` — an API from before outcomes were stored —
 * so the caller keeps the wording it had.
 */
export function outcomeWords(input: {
  outcome: string | null | undefined;
  reason?: string | null;
  error?: string | null;
  rejected?: string | null;
  fieldsUpdated?: string[] | null;
  /** Names a written field the way the card does. */
  labelFor?: (field: string) => string;
}): NamedOutcome | null {
  const { outcome } = input;
  if (!outcome) return null;
  const reason = input.reason ?? null;
  switch (outcome) {
    case 'promoted': {
      const fields = input.fieldsUpdated ?? [];
      const n = fields.length;
      const names = n > 0 && input.labelFor ? `: ${fields.map(input.labelFor).join(', ')}` : '';
      return {
        text: `Label read; ${n} ${n === 1 ? 'field' : 'fields'} changed${names}`,
        code: 'promoted',
      };
    }
    case 'proposed':
      return { text: 'Label read; waiting for your review', code: 'proposed' };
    case 'unresolved':
      return {
        text: reasonWords(reason) ?? "Couldn't tell what equipment this label is from",
        code: reason ? `unresolved / ${reason}` : 'unresolved',
      };
    case 'conflict':
      return {
        text: 'The catalog and the label disagree about what this is',
        code: reason ? `conflict / ${reason}` : 'conflict',
      };
    case 'error':
      return {
        text: `The label couldn't be read: ${input.error ?? 'no reason was recorded'}`,
        code: 'error',
      };
    case 'removed':
      // FS-783: the photo was removed while its read was running, so the read
      // was stopped and nothing it found was written.
      return { text: 'The photo was removed before its read finished', code: 'removed' };
    case 'rejected':
      return {
        text: `The read was refused: ${input.rejected ?? input.error ?? 'no code was recorded'}`,
        code: 'rejected',
      };
    default:
      return { text: `Label read (${outcome})`, code: outcome };
  }
}

/** Why a held review cannot be applied, named, with its code. */
export function heldWords(reason: string | null | undefined): NamedOutcome {
  return {
    text: reasonWords(reason) ?? "Couldn't tell what equipment this label is from",
    code: reason ?? 'held',
  };
}

// ── FS-876: fixing a model on the wrong record ────────────────────────────

/** A record kind as its tab names it, for the fix's sentences ("Outdoor unit"). */
const tabOf = (kind: PhotoReviewTab | null | undefined): string => (kind ? TAB_WORDS[kind] : 'other');

/**
 * The fix's button: "Move to Outdoor unit" when a ticked value moves, else
 * "Clear from Outdoor unit", named after the actual records.
 */
export function fixActionLabel(fix: ReviewFix, moves: boolean): string {
  return moves ? `Move to ${tabOf(fix.right.kind)}` : `Clear from ${tabOf(fix.wrong.kind)}`;
}

/**
 * FS-942 — a name the fix offers to change, as its checkbox says it, the
 * hover text, and a note for a name the owner's app saved (shown under the
 * box). The current name is shown trimmed; the server keeps it as stored.
 */
export function fixNameWords(name: ReviewFixName): { label: string; title: string; note: string | null } {
  const tab = tabOf(name.kind);
  const current = name.current.trim();
  if (name.record === 'right') {
    return {
      label: `Name the new ${tab} record "${name.suggested}"`,
      title: `Named for its brand and what the catalog says it is. Unticked, it is called "${current}".`,
      note: null,
    };
  }
  const saved = name.note === 'owner_saved';
  return {
    label: `Rename the ${tab} record from "${current}" to "${name.suggested}"`,
    title: `"${current}" is the brand or model this fix takes off the ${tab} record${saved ? '' : ', so it goes too'}. "${name.suggested}" is the app's name for a record nobody named.`,
    note: saved ? "The owner's app saved this name, so it changes only if ticked." : null,
  };
}

/**
 * FS-950 — what follows a fix: the model ends up on the record it belongs
 * on, which never had the catalog's values (the catalog offered them only
 * while the model sat on the wrong record, and held them there).
 */
export function fixCatalogAfterWords(model: string | null | undefined, rightTab: string): string {
  const name = model?.trim() ? `model ${model.trim()}` : 'this model';
  return `Then the catalog's values for ${name} come up for review on the ${rightTab} record, for anything it is missing.`;
}

/** Why a held review offers no fix, only Dismiss: what to do instead. */
export function fixReasonWords(fix: ReviewFix, model: string | null | undefined): string {
  const wrong = tabOf(fix.wrong.kind);
  const right = tabOf(fix.right.kind);
  const name = model?.trim() || 'this model';
  switch (fix.reason) {
    case 'model_differs': {
      const theirs = fix.right.model ? `model "${fix.right.model}"` : 'a different model';
      const typed = fix.right.model_person_decided ? ', entered by a person' : '';
      const what = fix.right.kind ? KIND_WORDS[fix.right.kind] : 'other unit';
      return `The ${right} record has ${theirs}${typed}. If ${name} is the ${what}'s model, change it in the ${right} card, then open this review again to clear it from the ${wrong} record.`;
    }
    case 'model_person_value':
      return `The model number on the ${wrong} record was entered by a person, so this review can't move or clear it. Change it in the ${wrong} card if it is wrong.`;
    case 'model_gone':
      return `The ${wrong} record no longer has model ${name}, so there is nothing to fix.`;
    case 'record_removed':
      return 'The record this review is about is no longer on file, so there is nothing to fix.';
    case 'no_component':
      return `The catalog does not say what kind of equipment model ${name} is, so there is no other record to put it on. Correct the record by hand if it is wrong.`;
    case 'several_records':
      return `This system has more than one ${fix.right.kind ? KIND_WORDS[fix.right.kind] : 'matching'} record, so which one these values belong on is not known. Move them by hand.`;
    case 'no_record':
      return `This system has no ${fix.right.kind ? KIND_WORDS[fix.right.kind] : 'matching'} record to put these values on.`;
    case 'unavailable':
      return "What this review can fix couldn't be worked out just now. Close it and open it again.";
    case 'not_supported':
    default:
      return 'Only a mix-up between the indoor unit and the outdoor unit can be fixed here. Correct these records by hand.';
  }
}

/** The photo checkbox's words: what ticking it does with this action. */
export function fixPhotoLabel(fix: ReviewFix, action: 'remove' | 'move'): string {
  return action === 'move'
    ? `Move the label photo to the ${fix.right.exists ? '' : 'new '}${tabOf(fix.right.kind)} record too`
    : `Also remove the label photo from the ${tabOf(fix.wrong.kind)} record`;
}

/** Why the photo cannot go with the fix, for the disabled checkbox's hover. */
export function fixPhotoBlockWords(block: ReviewFixPhotoBlock, fix: ReviewFix): string {
  const wrong = tabOf(fix.wrong.kind);
  switch (block) {
    case 'photo_already_removed':
      return 'This photo was already removed.';
    case 'photo_removal_off':
      return 'Removing photos is switched off on the server right now.';
    case 'photo_placement_off':
      return 'Moving photos between records is switched off on the server right now.';
    case 'also_on_other_record':
      return `The same photo is on another record of this system, and removing it would take it off that one too. Use Detach on the ${wrong} card's photo instead.`;
    case 'source_values_cite_photo':
      return `Values left on the ${wrong} record were read from this photo, so it stays there.`;
    case 'photo_busy':
      return `Another read of this photo on the ${wrong} record is still running, so it stays there for now.`;
    case 'source_evidence_unresolved':
      return `Some values on the ${wrong} record were read from this photo earlier and can't be traced to another copy, so it stays there.`;
    default:
      return `The photo stays on the ${wrong} record (${block}).`;
  }
}

/**
 * The finished line for a review closed by its fix: where the values went.
 * Null for any other decision (the dialog keeps its own words for those).
 */
export function fixDecisionWords(decision: PhotoReviewDecision | null | undefined): string | null {
  if (!decision?.target_path) return null;
  const tab = TAB_WORDS[kindOfPath(decision.target_path)];
  if (decision.kind === 'cleared') return `The values were cleared from the ${tab} record. This review is finished.`;
  if (decision.kind === 'moved') {
    return `The values were moved to the ${decision.created ? 'new ' : ''}${tab} record. This review is finished.`;
  }
  return null;
}
